<template>
  <div class="flex flex-col h-full">
    <!-- Scrollable List Area -->
    <div class="flex-1 min-h-0 overflow-y-auto">
      <div class="space-y-1.5 pr-2">
        <router-link
          v-for="entity in filteredEntities"
          :key="entity.entity_id"
          :to="`/entity/${entity.entity_id}/`"
          :class="getEntityCardClass(entity)"
          class="flex items-center justify-between rounded-lg p-2.5 hover:shadow-md transition-all group"
        >
          <!-- Entity Name -->
          <div class="flex-1 min-w-0 mr-3">
            <div class="sensitive font-medium text-sm truncate group-hover:text-primary-600 transition-colors"
                 :class="getEntityNameClass(entity)">
              {{ entity.entity_name || 'Unnamed Entity' }}
            </div>
            <!-- Engagement count badge -->
            <div class="text-xs mt-0.5" :class="getEngagementBadgeClass(entity)">
              {{ entity.engagement_count_6mo || 0 }} engagement{{ (entity.engagement_count_6mo || 0) === 1 ? '' : 's' }} (6mo)
            </div>
          </div>

          <!-- Attribution and Arrow -->
          <div class="flex items-center gap-2 shrink-0">
            <div class="text-xs font-semibold text-primary-600 min-w-[60px] text-right">
              {{ formatNumber(entity.total_attribution) }}
            </div>
            <font-awesome-icon
              :icon="['fas', 'arrow-right']"
              class="text-gray-400 text-xs"
            />
          </div>
        </router-link>

        <!-- Empty State -->
        <div
          v-if="filteredEntities.length === 0"
          class="flex flex-col items-center justify-center py-12 text-gray-500"
        >
          <font-awesome-icon :icon="['fas', 'inbox']" class="text-4xl mb-3 text-gray-400" />
          <p class="text-sm">
            {{ searchQuery ? 'No entities match your search' : 'No entities in this segment' }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { canAccessEntity } from '@/utils/access';

export default {
  name: 'EntityClassificationList',
  props: {
    entities: {
      type: Array,
      required: true
    },
    searchQuery: {
      type: String,
      default: ''
    }
  },
  computed: {
    filteredEntities() {
      if (!this.searchQuery) {
        return this.entities;
      }

      const query = this.searchQuery.toLowerCase();
      return this.entities.filter(entity => {
        const name = entity.entity_name || '';
        const id = String(entity.entity_id || '');
        return name.toLowerCase().includes(query) || id.includes(query);
      });
    }
  },
  methods: {
    formatNumber(value) {
      if (!value && value !== 0) return '-';
      const intValue = Math.floor(Number(value));
      return intValue.toLocaleString('en-US', { maximumFractionDigits: 0 });
    },

    getEngagementLevel(entity) {
      const count = entity.engagement_count_6mo || 0;
      if (count >= 10) return 'high';
      if (count >= 4) return 'medium';
      if (count >= 1) return 'low';
      return 'none';
    },

    getEntityCardClass(entity) {
      const level = this.getEngagementLevel(entity);
      let baseClass = 'bg-white border';

      if (!canAccessEntity(entity.entity_id)) {
        baseClass += " border-gray-200 opacity-70 pointer-events-none";
      }

      switch(level) {
        case 'high':
          return `${baseClass} border-green-300 hover:border-green-400`;
        case 'medium':
          return `${baseClass} border-blue-300 hover:border-blue-400`;
        case 'low':
          return `${baseClass} border-orange-300 hover:border-orange-400`;
        case 'none':
          return `${baseClass} border-gray-200 hover:border-primary-300`;
        default:
          return `${baseClass} border-gray-200 hover:border-primary-300`;
      }
    },

    getEntityNameClass(entity) {
      const level = this.getEngagementLevel(entity);

      switch(level) {
        case 'high':
          return 'text-green-900';
        case 'medium':
          return 'text-blue-900';
        case 'low':
          return 'text-orange-900';
        case 'none':
          return 'text-gray-900';
        default:
          return 'text-gray-900';
      }
    },

    getEngagementBadgeClass(entity) {
      const level = this.getEngagementLevel(entity);

      switch(level) {
        case 'high':
          return 'text-green-600 font-semibold';
        case 'medium':
          return 'text-blue-600 font-medium';
        case 'low':
          return 'text-orange-600 font-medium';
        case 'none':
          return 'text-gray-500';
        default:
          return 'text-gray-500';
      }
    }
  }
};
</script>

<style scoped>
/* Custom scrollbar */
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: #f3f4f6;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}
</style>
